/**
 * "The 90/180 applies to you — but not in Portugal" (6.5, benchmark §12–13):
 * the rule people bring the wrong idea of, explained where its row is. Who
 * holds a residence title is never counted by the 90/180 in the title's own
 * country; a day in another Schengen state counts twice — away from the
 * country, and towards the 90 days in any 180 there; a day outside Schengen
 * only as away. The EU's short-stay calculator is the visitors' — it says so
 * itself. With the person's own figures for the rule, when it has them.
 *
 * Something to read with care, so it opens as a reading panel beside the
 * page (owner, 19 Oct 2026: D6): its title fixed, the text scrolling, "Close"
 * at its foot.
 */
import {
  inoutClockHasNumber,
  inoutShortStayFiguresText,
  inoutShortStayRows,
  inoutWhereMarkText,
  type InOutClock,
  type InOutPastDay,
  type InOutWhereToday,
} from '@tonnie/heroes';
import { useLocale } from '@tonnie/locale';
import { Check, Globe } from '@tonnie/assets';
import {
  Badge,
  Button,
  DIALOG_BAR,
  DialogMedia,
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  cn,
} from '@tonnie/ui';

import { INOUT_CLASSES, INOUT_TONE } from '../lib/inout-tone';

/** A yes or a no at a glance — ✓ or — — said in words to a screen reader. */
function Mark({ counts }: { counts: boolean }) {
  const { t } = useLocale();
  return counts ? (
    <span className={cn('inline-flex', INOUT_CLASSES.text)}>
      <Check className="h-4 w-4" aria-hidden="true" />
      <span className="sr-only">{t('inout.shortStay.yes')}</span>
    </span>
  ) : (
    <span className="text-muted-foreground">
      <span aria-hidden="true">—</span>
      <span className="sr-only">{t('inout.shortStay.no')}</span>
    </span>
  );
}

/** Where the person may be, whether a day there counts as away and towards the 90/180 — today's marked. */
function WhereRow({
  where,
  away,
  shortStay,
  today,
  pastDay,
}: {
  where: string;
  away: boolean;
  shortStay: boolean;
  /** The engine says the person is here on the day read. */
  today: boolean;
  pastDay: InOutPastDay;
}) {
  const { t } = useLocale();
  return (
    <tr
      className={cn('border-b border-border last:border-0', today && INOUT_CLASSES.subtle)}
      data-testid="inout-short-stay-row"
      data-today={today ? 'true' : undefined}
    >
      <th scope="row" className="py-2 pr-3 font-medium text-foreground">
        <span className="flex flex-wrap items-center gap-2">
          {where}
          {today && (
            <Badge variant="outline" tone={INOUT_TONE} data-testid="inout-short-stay-here">
              {inoutWhereMarkText(t, pastDay)}
            </Badge>
          )}
        </span>
      </th>
      <td className="py-2 pr-3">
        <Mark counts={away} />
      </td>
      <td className="py-2">
        <Mark counts={shortStay} />
      </td>
    </tr>
  );
}

export function InOutShortStayExplainer({
  clock,
  country,
  whereToday,
  pastDay = null,
}: {
  /** The rule of days in the other Schengen states. */
  clock: InOutClock;
  /** The title's country, by name. */
  country: string;
  /** Where the engine says the person is on the day read — its row is marked. */
  whereToday: InOutWhereToday;
  /** The day a closed InOut is read as of, in words — null: today (H7). */
  pastDay?: InOutPastDay;
}) {
  const { t, formatNumber } = useLocale();
  const whereLabel: Record<ReturnType<typeof inoutShortStayRows>[number]['where'], string> = {
    HOST: t('inout.shortStay.inHost', { country }),
    OTHER_SCHENGEN: t('inout.shortStay.otherSchengen'),
    OUTSIDE_SCHENGEN: t('inout.shortStay.outsideSchengen'),
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="link"
          size="sm"
          className="h-auto self-start p-0 text-xs"
          data-testid="inout-short-stay-open"
        >
          {t('inout.shortStay.open')}
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        layout="scroll"
        className="w-full sm:max-w-lg"
        closeLabel={t('common.close')}
        data-testid="inout-short-stay"
      >
        <SheetHeader className="flex-row items-center gap-4 space-y-0">
          <DialogMedia icon={Globe} />
          <div className="min-w-0 space-y-1">
            <SheetTitle className="text-lg leading-tight">{t('inout.shortStay.title')}</SheetTitle>
            <SheetDescription>{t('inout.shortStay.lead', { country })}</SheetDescription>
          </div>
        </SheetHeader>
        <SheetBody className="space-y-5">
          <div className="overflow-x-auto rounded-lg border px-4">
            <table className="w-full text-left text-sm">
              <thead className="text-xs text-muted-foreground">
                <tr className="border-b border-border">
                  <th scope="col" className="py-2.5 pr-3 font-medium">
                    {t('inout.shortStay.where')}
                  </th>
                  <th scope="col" className="py-2.5 pr-3 font-medium">
                    {t('inout.shortStay.away', { country })}
                  </th>
                  <th scope="col" className="py-2.5 font-medium">
                    {t('inout.shortStay.shortStay')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {inoutShortStayRows(whereToday).map((row) => (
                  <WhereRow
                    key={row.where}
                    where={whereLabel[row.where]}
                    away={row.away}
                    shortStay={row.shortStay}
                    today={row.today}
                    pastDay={pastDay}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-foreground">{t('inout.shortStay.twice', { country })}</p>
          {inoutClockHasNumber(clock) && (
            <p
              className="rounded-lg bg-muted/50 p-3 text-sm tabular-nums text-foreground"
              data-testid="inout-short-stay-today"
            >
              {inoutShortStayFiguresText(clock, { t, formatNumber, pastDay })}
            </p>
          )}
          <p className="text-xs text-muted-foreground">{t('inout.shortStay.calculator')}</p>
          <p className="text-xs text-muted-foreground">{t('inout.shortStay.source')}</p>
        </SheetBody>
        <SheetFooter className={cn(DIALOG_BAR.footer, 'mt-0')}>
          <SheetClose asChild>
            <Button type="button" variant="ghost">
              {t('common.close')}
            </Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
